import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Clock,
  Plus,
  Loader2,
  Facebook,
  Mail,
  Calendar,
  Trash2,
  Edit,
  FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORM_ICONS = {
  facebook: Facebook,
  email: Mail
};

function DraftsList() {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDrafts();
  }, []);

  const loadDrafts = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/drafts`);
      setDrafts(response.data.drafts || []);
    } catch (error) {
      console.error('Load drafts error:', error);
      toast.error('Failed to load drafts');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Campaign Drafts</h1>
          <p className="text-white/60">Continue working on your saved drafts</p>
        </div>
        <Button
          onClick={() => navigate('/campaigns/create')}
          className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {/* Drafts List */}
      {drafts.length === 0 ? (
        <Card className="card">
          <CardContent className="p-12 text-center">
            <Clock className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Drafts Saved</h3>
            <p className="text-white/60 mb-4">Your saved campaign drafts will appear here</p>
            <Button
              onClick={() => navigate('/campaigns/create')}
              className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black"
            >
              <Plus className="w-4 h-4 mr-2" />
              Start New Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {drafts.map((draft, idx) => {
            const PlatformIcon = PLATFORM_ICONS[draft.platform] || FileText;
            
            return (
              <Card key={idx} className="card hover:border-white/20 transition-all">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-white/5 rounded-lg">
                      <PlatformIcon className="w-6 h-6 text-white/60" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-white truncate mb-1">
                        {draft.campaignName || 'Untitled Draft'}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-white/60">
                        <span>{draft.clientName || 'No client'}</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {draft.updatedAt ? new Date(draft.updatedAt).toLocaleDateString() : 'Not saved'}
                        </span>
                        <span>{draft.savedAds?.length || 0} / 3 ads</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/campaigns/create?draft=${idx}`)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Continue
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default DraftsList;
